import { createSlice } from "@reduxjs/toolkit";
import { OrgGeoPointData } from "../../actions/locations/locationsTypes";
import { ValuesCreateEditLocation } from "../map/mapTypes";
import {
  fetchLocations,
  fetchLocationsGroups,
} from "../../actions/locations/locationsAsyncActions";

import { UserInfo } from "../../actions/users/userTypes";
import { fetchUsersToLinkInToLocation } from "../../actions/locations/locationDetailsAsync";
import { DataSBFilterValues } from "../../actions/dataTable/DataTableTypes";
import { InarixGridFilterModel } from "../dataTable/data";

export interface LocationsState {
  locations: OrgGeoPointData[];
  locationCurrentName: OrgGeoPointData | null;
  locationId: number;
  locationNewName: string;
  error: string;
  errorPatch: string;
  loading: boolean;
  loadingPatch: boolean;
  msg: string;
  modal: boolean;
  initRows: any[];
  editLocation: ValuesCreateEditLocation;
  rowLocations: any[];
  locationsSelected: number[];
  usersSelected: any[];
  groups: any[];
  selectedLocationsToShow: any[];
  selectedGroupTitle: string;
  rawColumns: any[];
  createdGroups: {
    isCreatedGroupSuccess: boolean;
    isLoading: boolean;
    errorCreateGroup: string;
    groupName: string;
    isCreatingGroup: boolean;
    typeOfGroup: string;
    groupSelectedToMoveLocation: undefined | string;
    locationsSelectedAutoComplete: {
      allValuesSelected: boolean;
      valuesSelected: DataSBFilterValues[];
    };
  };

  users: UserInfo;
  loadingUsers: boolean;
  errorUsers: string;

  userSelectedAutocompleteLinkUsers: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  };
  runLocations: boolean;

  //custom filter component
  filterModel: InarixGridFilterModel;
  showFilterForm: boolean;
  preselectedFilterField: string | null;
}

export const initialState: LocationsState = {
  locations: [],
  locationCurrentName: null,
  locationId: 0,
  locationNewName: "",
  error: "",
  errorPatch: "",
  loading: false,
  loadingPatch: false,
  msg: "",
  modal: false,
  initRows: [],

  rawColumns: [
    {
      field: "name",
      sortable: true,
      width: 200,
    },

    {
      field: "location",
      sortable: true,
      align: "center",
      width: 150,
    },
    {
      field: "group",
      sortable: true,
      align: "center",
      width: 150,
    },
    {
      field: "capacity",
      sortable: false,
      align: "center",
      width: 250,
    },
  ],

  editLocation: {
    name: "",
    type: "",
    primaryContactName: "",
    telephone: "",
    email: "",
    address: "",
    notes: "",
    remoteId: "",
    countryName: "",
  },
  rowLocations: [],
  locationsSelected: [],
  usersSelected: [],
  groups: [],
  selectedLocationsToShow: [],
  selectedGroupTitle: "",
  createdGroups: {
    isCreatedGroupSuccess: false,
    isLoading: false,
    errorCreateGroup: "",
    groupName: "",
    isCreatingGroup: false,
    typeOfGroup: "",
    groupSelectedToMoveLocation: "",
    locationsSelectedAutoComplete: {
      allValuesSelected: false,
      valuesSelected: [],
    },
  },

  users: {
    total: 0,
    limit: 0,
    skip: 0,
    data: [],
  },
  loadingUsers: false,
  errorUsers: "",

  userSelectedAutocompleteLinkUsers: {
    allValuesSelected: false,
    valuesSelected: [],
  },

  runLocations: false,

  //custom filter component
  filterModel: { items: [] },
  showFilterForm: false,
  preselectedFilterField: null,
};

export const LocationsSlice = createSlice({
  name: "LocationsReducer",
  initialState,
  reducers: {
    //new custom filter component

    setFilterModel(state, action) {
      return {
        ...state,
        filterModel: action.payload,
      };
    },

    setShowFilterForm(state, action) {
      return {
        ...state,
        showFilterForm: action.payload,
      };
    },

    setPreselectedFilterField(state, action) {
      return {
        ...state,
        preselectedFilterField: action.payload,
      };
    },

    //new custom filter component

    setRunLocations(state, actions) {
      return {
        ...state,
        runLocations: actions.payload,
      };
    },
    resetUser(state) {
      return {
        ...state,
        users: initialState.users,
      };
    },

    setLocationColumns(state, action) {
      return {
        ...state,
        rawColumns: action.payload,
      };
    },

    setSelectGroupTitle(state, action) {
      return {
        ...state,
        selectedGroupTitle: action.payload,
      };
    },
    setSelectedLocationToShow(state, action) {
      return {
        ...state,
        selectedLocationsToShow: action.payload,
      };
    },
    setCreatedGroupValues(state, action) {
      return {
        ...state,
        createdGroups: action.payload,
      };
    },
    setSelectLocations(state, action) {
      return {
        ...state,
        locationsSelected: action.payload,
      };
    },

    setLoadingUserLoc(state, action) {
      return {
        ...state,
        loading: action.payload,
      };
    },
    setFilterFromLocToUserTabs(state, action) {
      return {
        ...state,
        filterModelLocation: {
          items: [
            {
              columnField: "location",
              operatorValue: "contains",
              value: action.payload,
            },
          ],
        },
      };
    },

    setModalLocations(state, action) {
      return {
        ...state,
        modal: action.payload,
      };
    },
    setUserLocationName(state, action) {
      return {
        ...state,
        locationCurrentName: action.payload,
      };
    },
    setUserLocationId(state, action) {
      return {
        ...state,
        locationId: action.payload,
      };
    },

    setNewLocationName(state, action) {
      return {
        ...state,
        locationNewName: action.payload,
      };
    },
    cleanUserLocationMessage(state) {
      return {
        ...state,
        msg: "",
        errorPatch: "",
        error: "",
      };
    },
    setNewValuesUpdateLocation(state, action) {
      return {
        ...state,
        editLocation: action.payload,
      };
    },

    setValuesAutoComplete(state, action) {
      return {
        ...state,
        userSelectedAutocompleteLinkUsers: action.payload,
      };
    },

    resetLocationsState() {
      return initialState;
    },
  },

  extraReducers(builder) {
    builder
      .addCase(fetchLocationsGroups.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLocationsGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchLocationsGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchUsersToLinkInToLocation.pending, (state) => {
        state.loadingUsers = true;
      })
      .addCase(fetchUsersToLinkInToLocation.fulfilled, (state, action) => {
        state.loadingUsers = false;
        state.users = action.payload;
      })
      .addCase(fetchUsersToLinkInToLocation.rejected, (state, action) => {
        state.loadingUsers = false;
        state.errorUsers = action.payload as string;
      });
  },
});

export const {
  //new custom filter component
  setFilterModel,
  setShowFilterForm,
  setPreselectedFilterField,
  //new custom filter component
  setRunLocations,
  setValuesAutoComplete,
  resetUser,
  setLocationColumns,
  setSelectGroupTitle,
  setSelectedLocationToShow,
  setCreatedGroupValues,
  setSelectLocations,
  setLoadingUserLoc,
  setFilterFromLocToUserTabs,
  setModalLocations,
  setUserLocationName,
  setUserLocationId,
  setNewLocationName,
  cleanUserLocationMessage,
  setNewValuesUpdateLocation,
  resetLocationsState,
} = LocationsSlice.actions;

export const Locations = LocationsSlice.reducer;
